/**
 * TransactionQuickActions - Quick Templates & Actions
 * 
 * Provides quick access to common transaction templates
 * (Import/Export moved to PageHeader - admin actions)
 */

import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { 
  Zap, 
  Plus,
  Copy,
  Repeat
} from 'lucide-react';

interface TransactionQuickActionsProps {
  onNewTransaction: () => void;
  onDuplicate?: () => void;
  onRecurring?: () => void;
}

export function TransactionQuickActions({
  onNewTransaction,
  onDuplicate,
  onRecurring,
}: TransactionQuickActionsProps) {
  const quickActions = [
    {
      label: 'Novo Lançamento',
      description: 'Criar do zero',
      icon: Plus,
      onClick: onNewTransaction,
      primary: true,
      shortcut: 'N',
    },
    {
      label: 'Recorrente',
      description: 'Configurar automático',
      icon: Repeat,
      onClick: onRecurring,
      shortcut: 'R',
    },
    {
      label: 'Duplicar',
      description: 'Copiar último',
      icon: Copy,
      onClick: onDuplicate,
      shortcut: 'D',
    },
  ];

  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-4">
        <Zap className="h-4 w-4 text-primary" />
        <h3 className="font-semibold">Ações Rápidas</h3>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {quickActions.map((action) => {
          const Icon = action.icon;
          return (
            <Button
              key={action.label}
              variant={action.primary ? 'default' : 'outline'}
              onClick={action.onClick}
              disabled={!action.onClick}
              className="w-full h-auto flex-col gap-2 p-3 relative group focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              aria-label={`${action.label}: ${action.description}`}
            >
              <Icon className="h-5 w-5" />
              <div className="text-center">
                <div className="text-xs font-medium">{action.label}</div>
                <div className="text-[10px] opacity-70">{action.description}</div>
              </div>
              
              {/* Keyboard Shortcut Badge */}
              <Badge 
                variant="secondary" 
                className="absolute top-1 right-1 h-5 px-1.5 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
              >
                {action.shortcut}
              </Badge>
            </Button>
          );
        })}
      </div>

      {/* Tips */}
      <div className="mt-4 p-3 bg-muted/50 rounded-lg">
        <p className="text-xs text-muted-foreground">
          <kbd className="px-1.5 py-0.5 bg-background border rounded text-[10px] font-mono">Ctrl</kbd>
          {' + '}
          <kbd className="px-1.5 py-0.5 bg-background border rounded text-[10px] font-mono">Letra</kbd>
          {' '}para atalhos rápidos
        </p>
      </div>
    </Card>
  );
}
